// Feature: time on site events for the Meta pixel.
//
// Publishes the custom event `theme:time_on_site` once the visitor has had the store open in a visible tab for 30,
// 60, 120 and 180 seconds in total. The time adds up across page views for the rest of the browser session
// (sessionStorage) and each milestone goes out once per session. Hidden tabs do not count.
// The theme has no Meta pixel of its own: a custom pixel under Settings > Customer events subscribes to the event
// and sends `customData.name` (TimeOnSite_30s, TimeOnSite_60s, TimeOnSite_120s, TimeOnSite_180s) to Meta.

const EVENT = 'theme:time_on_site';
const STORAGE_KEY = 'time-on-site';
const MILESTONES = [30, 60, 120, 180];

/** Visible time this session in ms, up to the last pause, and how many milestones went out. */
let state = { ms: 0, sent: 0 };
/** performance.now() when the tab last became visible, null while it is hidden. */
let visibleSince = null;
let timer;

function read() {
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || 'null');
    if (Number.isFinite(stored?.ms) && Number.isInteger(stored?.sent)) return stored;
  } catch {
    // Storage blocked or unreadable: count from zero on this page.
  }
  return { ms: 0, sent: 0 };
}

function save() {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage blocked: the time only adds up within one page view.
  }
}

function elapsed() {
  return state.ms + (visibleSince === null ? 0 : performance.now() - visibleSince);
}

/** Sends every milestone that is due, then waits for the next one. */
function check() {
  clearTimeout(timer);
  while (state.sent < MILESTONES.length && elapsed() >= MILESTONES[state.sent] * 1000) {
    // Not loaded yet or blocked: the milestone stays due and goes out on the next page view or visible stretch.
    if (!window.Shopify?.analytics?.publish) return;
    const seconds = MILESTONES[state.sent];
    window.Shopify.analytics.publish(EVENT, { name: `TimeOnSite_${seconds}s`, seconds });
    state.sent += 1;
    save();
  }
  if (state.sent < MILESTONES.length) timer = setTimeout(check, MILESTONES[state.sent] * 1000 - elapsed());
}

function resume() {
  if (visibleSince !== null || document.visibilityState !== 'visible') return;
  visibleSince = performance.now();
  check();
}

function pause() {
  if (visibleSince === null) return;
  state.ms = elapsed();
  visibleSince = null;
  clearTimeout(timer);
  save();
}

function start() {
  state = read();
  document.addEventListener('visibilitychange', () => (document.visibilityState === 'visible' ? resume() : pause()));
  window.addEventListener('pagehide', pause);
  // Back/forward cache: other pages have added time since this one was left.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    state = read();
    resume();
  });
  resume();
}

// A prerendered page would read the session time before the visitor leaves the current page.
if (document.prerendering) document.addEventListener('prerenderingchange', start, { once: true });
else start();
